import React, { useState } from "react";
import { AlertCircle, Copy, Plus } from "lucide-react";
import { bankTransferAutoConfirmApiService } from "../../../../infrastructure/api/company/bank-transfer-auto-confirm-api-service";
import { companyBankTransferUseCase } from "../../../../di/container";
import type { AutoConfirmSubscription } from "../../../../domain/types/bank-transfer-auto-confirm";
import type { BankTransferEntity } from "../../../../domain/types/company-bank-transfer";
import {
  BankMark,
  MAX_AUTO_CONFIRM_ACCOUNTS,
  bankLabel,
  statusCopy,
} from "./shared";
import { money } from "../bankTransferFormat";

const LIVE = ["ACTIVE", "ACTIVATING", "PAST_DUE", "CANCEL_REQUESTED"];

const date = (v?: string) =>
  v
    ? new Date(v).toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

/** 목록 곁줄용. 긴 한글 날짜는 좁은 칸에서 줄바꿈돼 행마다 높이가 어긋난다 */
const shortDate = (v?: string) => {
  if (!v) return "—";
  const d = new Date(v);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
};

/** 계좌 한 줄. 자동확인을 쓰는 계좌면 구독이 함께 붙는다 */
type Row = { account: BankTransferEntity; subscription?: AutoConfirmSubscription };

/**
 * 계좌 관리 탭.
 *
 * **주인공은 계좌지 구독이 아니다.** 구독을 기준으로 그리면 요금을 내지 않는 계좌가
 * 화면에서 사라지는데, 그 계좌도 고객 결제창에는 그대로 보인다.
 *
 * 카드가 아니라 **행 목록**이다. 계좌는 10개까지 늘 수 있는데 카드로 늘어놓으면
 * 매일 보는 화면이 그만큼 길어진다.
 */
export const AccountTab: React.FC<{
  companyId: number;
  accounts: BankTransferEntity[];
  subscriptions: AutoConfirmSubscription[];
  readOnly: boolean;
  /** 자동확인을 이용 중인 회사인지. 이때는 수기 등록을 열어주지 않는다 */
  autoMode: boolean;
  onAddAuto: () => void;
  onAddManual: () => void;
  onEditAuto: (subscription: AutoConfirmSubscription) => void;
  onEditManual: (account: BankTransferEntity) => void;
  onChanged: () => Promise<void> | void;
}> = ({
  companyId,
  accounts,
  subscriptions,
  readOnly,
  autoMode,
  onAddAuto,
  onAddManual,
  onEditAuto,
  onEditManual,
  onChanged,
}) => {
  const [busyId, setBusyId] = useState<number>();
  const [error, setError] = useState("");

  const live = subscriptions.filter((it) => LIVE.includes(it.status));
  const rows: Row[] = accounts.map((account) => ({
    account,
    subscription: live.find((it) => it.bankTransferId === account.id),
  }));
  const visible = accounts.filter((it) => it.isEnabled).length;
  // 해지한 계좌도 종료일까지는 살아 있다 — 요금도 나가고 한도도 차지한다.
  // 그래서 위 숫자에는 포함하되 목록만 아래로 내린다.
  const canceled = rows.filter(
    (it) => it.subscription?.status === "CANCEL_REQUESTED",
  );
  const using = rows.filter(
    (it) => it.subscription?.status !== "CANCEL_REQUESTED",
  );

  const run = async (id: number, job: () => Promise<unknown>, fail: string) => {
    if (readOnly || busyId) return;
    setBusyId(id);
    setError("");
    try {
      await job();
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : fail);
      await Promise.resolve(onChanged()).catch(() => undefined);
    } finally {
      setBusyId(undefined);
    }
  };

  /**
   * 쇼핑몰 노출 토글.
   *
   * 자동확인 계좌는 구독 쪽 API 로 바꾼다 — 그쪽이 계좌를 잠그고 다루기 때문이다.
   * 수기 계좌는 계좌 자체를 고치면 된다.
   */
  const toggle = (row: Row) =>
    run(
      row.account.id,
      () =>
        row.subscription
          ? bankTransferAutoConfirmApiService.setShopVisible(
              companyId,
              row.account.id,
              !row.account.isEnabled,
            )
          : companyBankTransferUseCase.updateBankTransfer(
              companyId,
              row.account.id,
              {
                bank: row.account.bank,
                accountNumber: row.account.accountNumber,
                accountHolder: row.account.accountHolder,
                isEnabled: !row.account.isEnabled,
              },
            ),
      "노출 설정을 바꾸지 못했습니다.",
    );

  const remove = (row: Row) => {
    if (
      !window.confirm(
        `${bankLabel(row.account.bank)} ${row.account.accountNumber} 계좌를 지울까요?\n\n` +
          "고객 결제창에서 바로 사라집니다. 이미 이 계좌로 안내한 주문이 있는지 확인해 주세요.",
      )
    )
      return;
    return run(
      row.account.id,
      () =>
        companyBankTransferUseCase.deleteBankTransfer(
          companyId,
          row.account.id,
        ),
      "계좌를 지우지 못했습니다.",
    );
  };

  const cancel = (row: Row) => {
    const subscription = row.subscription;
    if (!subscription) return;
    if (
      !window.confirm(
        `${date(subscription.currentPeriodEndsAt)}에 해지됩니다.\n\n` +
          "그때까지는 그대로 이용할 수 있고, 다음 결제는 이뤄지지 않습니다.\n" +
          "그 전까지는 [해지 취소]로 되돌릴 수 있습니다.",
      )
    )
      return;
    return run(
      row.account.id,
      () => bankTransferAutoConfirmApiService.cancel(companyId, subscription.id),
      "해지하지 못했습니다.",
    );
  };

  /** 끊기기 전에 마음을 바꾼 경우. 남은 이용기간을 그대로 이어 쓴다 — 결제가 새로 일어나지 않는다 */
  const resume = (row: Row) => {
    const subscription = row.subscription;
    if (!subscription) return;
    return run(
      row.account.id,
      () => bankTransferAutoConfirmApiService.resume(companyId, subscription.id),
      "해지를 취소하지 못했습니다.",
    );
  };

  const rowProps = {
    readOnly,
    busyId,
    autoMode,
    onToggle: toggle,
    onEditAuto,
    onEditManual,
    onRemove: remove,
    onCancel: cancel,
    onResume: resume,
  };

  return (
    <div>
      {error && (
        <div className="mb-5 flex items-center gap-2 rounded-[12px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-3 text-sm text-[#4285F4]">
          <AlertCircle size={17} /> {error}
        </div>
      )}

      <div className="mb-5 grid grid-cols-1 divide-y divide-[#EEEEEE] rounded-[12px] border border-[#E0E0E0] bg-[#FAFAFA] sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <Stat
          label="전체 등록 계좌"
          value={`${accounts.length}개`}
          note="등록된 무통장 입금 계좌 수"
        />
        <Stat
          label="쇼핑몰 노출 중"
          value={`${visible}개`}
          note="고객 결제창에 보이는 계좌"
          dot
        />
        <Stat
          label="추가 등록 가능"
          value={`${Math.max(0, MAX_AUTO_CONFIRM_ACCOUNTS - accounts.length)}개`}
          note={`최대 ${MAX_AUTO_CONFIRM_ACCOUNTS}개까지 등록 가능`}
        />
      </div>

      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[16px] font-bold text-[#222222]">등록한 계좌</h3>
        {/*
          자동확인을 쓰는 동안에는 수기 등록을 열어주지 않는다. 한 회사에 자동으로 확인되는
          계좌와 그렇지 않은 계좌가 섞이면, 입금이 들어올 때마다 어느 쪽인지 따져야 한다.
        */}
        <button
          onClick={autoMode ? onAddAuto : onAddManual}
          disabled={readOnly || accounts.length >= MAX_AUTO_CONFIRM_ACCOUNTS}
          title={
            accounts.length >= MAX_AUTO_CONFIRM_ACCOUNTS
              ? `계좌는 최대 ${MAX_AUTO_CONFIRM_ACCOUNTS}개까지 등록할 수 있습니다.`
              : undefined
          }
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#4285F4] px-4 py-2.5 text-[13px] font-bold text-white hover:bg-[#3367D6] disabled:cursor-not-allowed disabled:bg-[#BBBBBB]"
        >
          <Plus size={16} /> {autoMode ? "자동확인 계좌 추가" : "계좌 등록"}
        </button>
      </div>

      <div className="flex flex-col gap-2.5">
        {using.map((row) => (
          <AccountRow key={row.account.id} row={row} {...rowProps} />
        ))}
      </div>

      {/*
        해지한 계좌는 아래로 내린다. 매일 보는 것은 이용 중인 계좌이고,
        해지한 것은 종료일까지 남아 있을 뿐 손댈 일이 없다.
      */}
      {canceled.length > 0 && (
        <div className="mt-7">
          <div className="mb-3 flex items-baseline gap-2">
            <h3 className="text-[16px] font-bold text-[#222222]">
              해지한 계좌
            </h3>
            <span className="text-[12px] text-[#888888]">
              종료일에 자동으로 해지됩니다.
            </span>
          </div>
          <div className="flex flex-col gap-2.5">
            {canceled.map((row) => (
              <AccountRow key={row.account.id} row={row} {...rowProps} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 flex items-start gap-3 rounded-[12px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-3.5">
        <AlertCircle size={17} className="mt-0.5 shrink-0 text-[#4285F4]" />
        <p className="text-[12px] leading-6 text-[#4285F4]/90">
          {autoMode ? (
            <>
              계좌당 월 {money(10000)}이 청구됩니다.{" "}
              <b>쇼핑몰 노출을 꺼도 입금 확인과 요금은 계속됩니다.</b>
              <br />
              자동확인을 쓰시는 동안에는 계좌를 직접 등록할 수 없습니다.
            </>
          ) : (
            <>
              요금이 없는 계좌입니다. 입금은 은행 앱에서 확인하시고, 입금 확인
              탭에서 <b>결제 확인</b>을 눌러 주문을 접수해 주세요.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

const AccountRow = ({
  row,
  readOnly,
  busyId,
  autoMode,
  onToggle,
  onEditAuto,
  onEditManual,
  onRemove,
  onCancel,
  onResume,
}: {
  row: Row;
  readOnly: boolean;
  busyId?: number;
  autoMode: boolean;
  onToggle: (row: Row) => void;
  onEditAuto: (subscription: AutoConfirmSubscription) => void;
  onEditManual: (account: BankTransferEntity) => void;
  onRemove: (row: Row) => void;
  onCancel: (row: Row) => void;
  onResume: (row: Row) => void;
}) => {
  const { account, subscription } = row;
  const busy = busyId === account.id;

  return (
    <div
      className={`grid grid-cols-1 items-center gap-4 rounded-[12px] border border-[#E0E0E0] px-5 py-4 sm:grid-cols-[minmax(170px,1.1fr)_minmax(180px,1.1fr)_150px_112px_116px] ${
        account.isEnabled ? "bg-white" : "bg-[#FAFAFA]"
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <BankMark bank={account.bank} small />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-[#222222]">
            {bankLabel(account.bank)}
          </div>
          <div className="truncate text-xs text-[#888888]">
            예금주 {account.accountHolder}
          </div>
        </div>
      </div>

      <div className="min-w-0">
        <div className="text-[11px] text-[#888888]">계좌번호</div>
        <div className="flex items-center gap-1.5 text-sm font-medium text-[#222222]">
          <span className="truncate">{account.accountNumber}</span>
          {/* 고객에게 알려줄 때 그대로 쓰는 값이라 숫자만 복사한다 */}
          <button
            onClick={() => void navigator.clipboard.writeText(account.accountNumber)}
            className="text-[#BBBBBB] hover:text-[#4285F4]"
            title="복사"
          >
            <Copy size={13} />
          </button>
        </div>
      </div>

      <div>
        <div className="text-[11px] text-[#888888]">상태</div>
        {subscription ? (
          <>
            <span
              className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${
                statusCopy[subscription.status]?.[1] ??
                "bg-[#F5F5F5] text-[#888888]"
              }`}
            >
              {statusCopy[subscription.status]?.[0] ?? subscription.status}
            </span>
            <div className="mt-1 whitespace-nowrap text-[11px] text-[#888888]">
              {subscription.status === "CANCEL_REQUESTED"
                ? `${shortDate(subscription.currentPeriodEndsAt)} 종료`
                : subscription.status === "PAST_DUE"
                  ? `${shortDate(subscription.graceEndsAt)} 재시도`
                  : `다음 결제 ${shortDate(subscription.nextBillingAt)}`}
            </div>
          </>
        ) : (
          <>
            <span className="inline-block rounded bg-[#F5F5F5] px-2 py-0.5 text-[11px] font-medium text-[#555555]">
              수기 확인
            </span>
            {/*
              자동확인을 쓰는 회사에 수기 계좌가 남아 있으면, 사장님은 그 계좌도 저절로
              확인되는 줄 알고 방치한다. 눈에 걸리게 두고 정리를 권한다.
            */}
            <div className="mt-1 whitespace-nowrap text-[11px] text-[#B54708]">
              {autoMode ? "계좌를 다시 등록해 주세요" : "요금 없음"}
            </div>
          </>
        )}
      </div>

      <div>
        <div className="text-[11px] text-[#888888]">쇼핑몰 노출</div>
        <button
          onClick={() => onToggle(row)}
          disabled={readOnly || busy}
          aria-label="쇼핑몰 노출"
          className={`relative mt-1 h-[23px] w-[40px] rounded-full transition disabled:opacity-45 ${
            account.isEnabled ? "bg-[#4285F4]" : "bg-[#E0E0E0]"
          }`}
        >
          <span
            className={`absolute top-[3px] h-[17px] w-[17px] rounded-full bg-white shadow transition-all ${
              account.isEnabled ? "left-[20px]" : "left-[3px]"
            }`}
          />
        </button>
      </div>

      {/*
        해지한 계좌에는 되돌리기만 남긴다.
        끊기기 전까지는 연동사 계좌가 살아 있어 언제든 이어 쓸 수 있다.
        수정은 빼 둔다 — 나가는 계좌의 자격증명을 고칠 일은 드물고,
        버튼이 여럿이면 무엇이 되돌리는 것인지 헷갈린다.
      */}
      <div className="flex items-center justify-end gap-1.5">
        {subscription?.status === "CANCEL_REQUESTED" ? (
          <RowButton onClick={() => onResume(row)} disabled={readOnly || busy} blue>
            해지 취소
          </RowButton>
        ) : subscription ? (
          <>
            <RowButton onClick={() => onEditAuto(subscription)} disabled={readOnly}>
              수정
            </RowButton>
            <RowButton onClick={() => onCancel(row)} disabled={readOnly || busy}>
              해지
            </RowButton>
          </>
        ) : (
          <>
            {/* 자동확인 중이라면 이 계좌는 정리 대상이다. 고칠 길을 열어두면 그대로 눌러앉는다 */}
            {!autoMode && (
              <RowButton onClick={() => onEditManual(account)} disabled={readOnly}>
                수정
              </RowButton>
            )}
            <RowButton onClick={() => onRemove(row)} disabled={readOnly || busy} danger>
              삭제
            </RowButton>
          </>
        )}
      </div>

      {subscription?.lastError && (
        <div className="col-span-full rounded-[8px] bg-[#EBF3FF] px-3 py-2 text-[12px] text-[#4285F4]">
          {subscription.lastError}
        </div>
      )}
    </div>
  );
};

const RowButton = ({
  onClick,
  disabled,
  blue = false,
  danger = false,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  blue?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`rounded-md border px-3 py-1.5 text-[12px] font-medium disabled:border-[#E0E0E0] disabled:text-[#BBBBBB] ${
      blue
        ? "border-[#4285F4] text-[#4285F4] hover:bg-[#EBF3FF]"
        : danger
          ? "border-[#E0E0E0] text-[#F24A4A] hover:bg-[#FEF2F2]"
          : "border-[#E0E0E0] text-[#555555] hover:bg-[#F5F5F5]"
    }`}
  >
    {children}
  </button>
);

const Stat = ({
  label,
  value,
  note,
  dot,
}: {
  label: string;
  value: string;
  note: string;
  dot?: boolean;
}) => (
  <div className="px-5 py-4">
    <div className="flex items-center gap-1.5 text-[12px] font-medium text-[#555555]">
      {dot && <span className="h-[7px] w-[7px] rounded-full bg-[#12B76A]" />}
      {label}
    </div>
    <div className="mt-1 text-[22px] font-bold text-[#222222]">{value}</div>
    <div className="text-[12px] text-[#888888]">{note}</div>
  </div>
);
